import { useThreadMessages } from "@convex-dev/agent/react";
import type { ChatStatus } from "ai";
import { useMutation, useQuery } from "convex/react";
import { SparklesIcon } from "lucide-react";
import { useQueryState } from "nuqs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Suggestion } from "@/components/ai-elements/suggestion";
import { useAgentHealthPoller } from "@/hooks/useAgentHealthPoller";
import { authClient } from "@/lib/authClient";
import { getSessionId } from "@/lib/posthog";
import { usePostHogFlag } from "@/lib/usePostHogFlag";
import {
	type InteractionAnswer,
	parseInteraction,
} from "../../../../email-assistant/core/toolInteractions";
import { invocationReferencesFor } from "../../../../email-assistant/core/workflowMentions";
import { api } from "../../../convex/_generated/api";
import { CONTEXT_COMPACTION_MARKER } from "../../../convex/agent/heavyTools";
import {
	activeWorkflowStateFor,
	buildToolResultMap,
	type ChatMessageLike,
	latestInteractionCall,
	workflowFactRegistersFor,
} from "../../modules/threads/messageHelpers";
import { ActiveInteractionDock } from "./ActiveInteractionDock";
import ChatComposer from "./ChatComposer";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import { DEFAULT_CHAT_MODEL, isChatModelId } from "./chatModels";
import { FactRegisterContent } from "./FactRegisterCard";
import type { RunOutcome } from "./RunOutcomeCard";
import {
	BlankChatPane,
	ThreadImpersonationPrompt,
} from "./ThreadImpersonationPrompt";
import { WorkflowTrackerCard } from "./WorkflowTrackerCard";

/**
 * PostHog flag: variant key = model id. Targeted per-user + per-tenant
 * (`activeOrganizationId`); falls back to DEFAULT_CHAT_MODEL if unset/unknown/unloaded.
 */
const CHAT_DEFAULT_MODEL_FLAG = "chat-default-model";

function resolveFlagDefaultModel(flagValue: unknown): string {
	return isChatModelId(flagValue) ? flagValue : DEFAULT_CHAT_MODEL;
}

const STARTER_PROMPTS = [
	"Summarize my unread email conversations from this week.",
	"What's the status of my newest matter?",
	"Draft a follow-up to the last client who emailed about documents.",
	"Walk me through opening a new matter.",
];

function generateChatId(userKey: string) {
	const rand = Math.random().toString(36).slice(2, 8);
	return `chat:${userKey}:${Date.now()}:${rand}`;
}

/**
 * Live chat for a thread. Owns the single `useThreadMessages` subscription;
 * reads model straight off the thread row — no local state, no effects.
 */
function ActiveChat({
	clericalThreadId,
	focusMessageId,
}: {
	clericalThreadId: string;
	focusMessageId?: string;
}) {
	const send = useMutation(api.agent.index.send);
	const cancel = useMutation(api.agent.index.cancel);
	const dismissRunOutcome = useMutation(api.agent.index.dismissRunOutcome);
	const setModelMutation = useMutation(api.agent.queries.setChatThreadModel);
	const thread = useQuery(api.agent.queries.getChatThread, {
		clericalThreadId,
	});
	const workflows = useQuery(
		api.agent.workflows.workflowIndexQueries.listWorkflows,
		{},
	);
	const skills = useQuery(api.agent.skills.library.listSkillsForOperators, {});
	const toolResponses = useQuery(api.agent.queries.listToolResponses, {
		clericalThreadId,
	});
	const recommendedModelIds = useQuery(
		api.llmSubscriptions.accounts.listRecommendedModelIds,
		{},
	);
	const flagModel = usePostHogFlag(CHAT_DEFAULT_MODEL_FLAG);
	// Retired sticky models fall back to the flag default (no data migration).
	const stickyModel = thread?.model;
	const model = isChatModelId(stickyModel)
		? stickyModel
		: resolveFlagDefaultModel(flagModel);
	// In-flight tool name for the live Steps panel ("__llm_streaming" = model time).
	const activeToolName =
		thread?.lastActiveTool &&
		thread.lastActiveTool !== "__llm_streaming" &&
		thread.lastActiveTool !== CONTEXT_COMPACTION_MARKER
			? thread.lastActiveTool
			: null;

	const messagesQuery = useThreadMessages(
		api.agent.queries.listThreadMessages,
		{ threadId: clericalThreadId },
		{ initialNumItems: 50, stream: true },
	);
	type StreamingMessage = { streaming?: boolean; status?: string };
	const messages = messagesQuery.results as unknown as StreamingMessage[];

	const status: ChatStatus = useMemo(() => {
		const last = messages.at(-1);
		if (last?.streaming) return "streaming";
		if (last?.status === "pending") return "submitted";
		return "ready";
	}, [messages]);

	// Polls health every 30s while streaming. If the agent dies before its catch
	// runs, the mutation's finalize action sets lastRunOutcome="crashed" (thread
	// live-query auto-updates the card). Gated by `enabled` to skip idle/terminal turns.
	useAgentHealthPoller({
		clericalThreadId,
		isStreaming: status === "streaming" || status === "submitted",
		isTerminal: Boolean(thread?.lastRunOutcome),
	});

	const handleModelChange = (id: string) => {
		void setModelMutation({ clericalThreadId, model: id });
	};

	const handleSubmit = (text: string) => {
		const { workflowRefs, skillRefs } = invocationReferencesFor(
			text,
			workflows,
			skills,
		);
		void send({
			clericalThreadId,
			message: text,
			model,
			kind: "chat",
			posthogSessionId: getSessionId(),
			...(workflowRefs.length > 0 ? { workflowRefs } : {}),
			...(skillRefs.length > 0 ? { skillRefs } : {}),
		});
	};

	const handleAnswerQuestions = (answerMessage: string) => {
		void send({
			clericalThreadId,
			message: answerMessage,
			model,
			kind: "chat",
			posthogSessionId: getSessionId(),
		});
	};

	// Active interaction without response → docked prompt.
	const answeredToolCallIds = useMemo(
		() => new Set((toolResponses ?? []).map((r) => r.toolCallId)),
		[toolResponses],
	);
	const pendingInteraction = useMemo(() => {
		const call = latestInteractionCall(
			messagesQuery.results as unknown as ChatMessageLike[],
		);
		if (!call || answeredToolCallIds.has(call.toolCallId)) return null;
		return parseInteraction(call.toolName, call.input, call.toolCallId);
	}, [messagesQuery.results, answeredToolCallIds]);

	const submitInteraction = (
		answerMessage: string,
		response: { toolName: string; answers: InteractionAnswer[] },
	) => {
		if (!pendingInteraction) return;
		void send({
			clericalThreadId,
			message: answerMessage,
			model,
			kind: "chat",
			posthogSessionId: getSessionId(),
			respondingToToolCallId: pendingInteraction.toolCallId,
			structuredResponse: response,
		});
	};

	const handleStop = () => {
		void cancel({ clericalThreadId });
	};

	// Retryable outcomes (timed_out/failed/crashed) resend "continue" — history
	// already has the ask + partial work, so this resumes rather than restarts.
	const handleRetryOutcome = () => {
		void dismissRunOutcome({ clericalThreadId });
		void send({
			clericalThreadId,
			message: "continue",
			model,
			kind: "chat",
			posthogSessionId: getSessionId(),
		});
	};

	const outcome = thread?.lastRunOutcome as RunOutcome | undefined;

	const workflowTracker = useMemo(() => {
		const rows = messagesQuery.results as unknown as ChatMessageLike[];
		return activeWorkflowStateFor(
			rows,
			buildToolResultMap(rows),
			thread?.inlineWorkflowCancelledRunId,
		);
	}, [messagesQuery.results, thread?.inlineWorkflowCancelledRunId]);

	const workflowFactRegisters = useMemo(() => {
		const rows = messagesQuery.results as unknown as ChatMessageLike[];
		return workflowFactRegistersFor(rows, buildToolResultMap(rows));
	}, [messagesQuery.results]);

	const renderAfterSteps = useCallback(
		(turn: { messages: ReadonlyArray<{ _id: string }> }) => {
			const nodes: React.ReactNode[] = [];
			for (const message of turn.messages) {
				for (const [index, register] of workflowFactRegisters.entries()) {
					if (register.messageId !== message._id) continue;
					const isLatest = index === workflowFactRegisters.length - 1;
					nodes.push(
						<div
							key={`fact-register-${register.messageId}-${register.version}`}
							className="not-prose w-full"
						>
							<FactRegisterContent
								factRegisterId={`${clericalThreadId}-${register.messageId}-v${register.version}`}
								version={register.version}
								artifact={
									register.docxWebUrl
										? { webUrl: register.docxWebUrl }
										: undefined
								}
								facts={register.facts}
								defaultCollapsed={!isLatest}
							/>
						</div>,
					);
				}
			}
			return nodes.length > 0 ? nodes : null;
		},
		[workflowFactRegisters, clericalThreadId],
	);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<ChatHeader clericalThreadId={clericalThreadId} model={model} />
			{/* `relative` so the floating workflow tracker pins to the chat area
			    only — it overlays the message list and stays put as it scrolls. */}
			<div className="relative min-h-0 flex-1">
				<ChatMessages
					clericalThreadId={clericalThreadId}
					messages={messagesQuery.results}
					status={messagesQuery.status}
					loadMore={messagesQuery.loadMore}
					onAnswerQuestions={handleAnswerQuestions}
					answerInFlight={status === "submitted" || status === "streaming"}
					activeToolName={activeToolName}
					outcome={outcome}
					outcomeStatusText={thread?.statusText}
					onRetryOutcome={handleRetryOutcome}
					renderAfterSteps={renderAfterSteps}
					focusMessageId={focusMessageId}
					toolResponses={toolResponses}
					contextCompaction={thread?.contextCompaction}
					turnContinuations={thread?.turnContinuations}
				/>
				{workflowTracker ? (
					<WorkflowTrackerCard
						run={workflowTracker}
						clericalThreadId={clericalThreadId}
					/>
				) : null}
			</div>
			<div className="shrink-0 border-t bg-background/80 p-3 backdrop-blur">
				<div className="mx-auto w-full max-w-3xl">
					{pendingInteraction ? (
						<ActiveInteractionDock
							interaction={pendingInteraction}
							onSubmit={submitInteraction}
							disabled={status === "submitted" || status === "streaming"}
						/>
					) : (
						<ChatComposer
							draftScope={clericalThreadId}
							status={status}
							model={model}
							recommendedModelIds={recommendedModelIds}
							onModelChange={handleModelChange}
							onSubmit={handleSubmit}
							onStop={handleStop}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

function EmptyState({ userKey }: { userKey: string }) {
	const navigate = useNavigate();
	const send = useMutation(api.agent.index.send);
	const workflows = useQuery(
		api.agent.workflows.workflowIndexQueries.listWorkflows,
		{},
	);
	const skills = useQuery(api.agent.skills.library.listSkillsForOperators, {});
	// Firm-gated: adds a "File scanned notices" starter pill when configured.
	const noticeCfg = useQuery(api.agent.queries.getNoticeFilingConfig, {});
	const recommendedModelIds = useQuery(
		api.llmSubscriptions.accounts.listRecommendedModelIds,
		{},
	);
	const flagModel = usePostHogFlag(CHAT_DEFAULT_MODEL_FLAG);
	const [model, setModel] = useState<string>(DEFAULT_CHAT_MODEL);

	// Hydrate picker default from the flag once it loads; guarded so a user
	// pick during the brief load window always wins.
	const userTouchedModel = useRef(false);
	const hydratedFromFlag = useRef(false);
	if (
		!hydratedFromFlag.current &&
		!userTouchedModel.current &&
		isChatModelId(flagModel)
	) {
		hydratedFromFlag.current = true;
		if (flagModel !== model) setModel(flagModel);
	}
	const handleModelChange = (id: string) => {
		userTouchedModel.current = true;
		setModel(id);
	};

	const handleFirstSend = (text: string) => {
		const newId = generateChatId(userKey);
		const { workflowRefs, skillRefs } = invocationReferencesFor(
			text,
			workflows,
			skills,
		);
		void send({
			clericalThreadId: newId,
			message: text,
			model,
			kind: "chat",
			posthogSessionId: getSessionId(),
			...(workflowRefs.length > 0 ? { workflowRefs } : {}),
			...(skillRefs.length > 0 ? { skillRefs } : {}),
		});
		navigate(`/chat/${encodeURIComponent(newId)}`);
	};

	return (
		<div className="flex h-full min-h-0 flex-col">
			<header className="h-12 shrink-0 border-b bg-background/80 px-4 pl-14 backdrop-blur" />
			<div className="flex flex-1 items-center justify-center">
				<div className="mx-auto w-full max-w-2xl px-4">
					<div className="flex flex-col items-center gap-4 pb-8">
						<div className="flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
							<SparklesIcon className="size-8" />
						</div>
						<div className="text-center">
							<h1 className="text-2xl font-semibold">
								How can Clerical help today?
							</h1>
							<p className="mt-1 text-sm text-muted-foreground">
								Ask anything — case lookups, drafts, scheduling, codex notes.
							</p>
						</div>
						<div className="flex flex-wrap justify-center gap-2 pt-2">
							{[
								...STARTER_PROMPTS,
								...(noticeCfg?.enabled
									? ["File the scanned government notices from the copier."]
									: []),
							].map((p) => (
								<Suggestion
									key={p}
									suggestion={p}
									onClick={(s) => handleFirstSend(s)}
									className="max-w-full whitespace-normal text-left"
								/>
							))}
						</div>
					</div>
				</div>
			</div>
			<div className="shrink-0 border-t bg-background/80 p-3 backdrop-blur">
				<div className="mx-auto w-full max-w-3xl">
					<ChatComposer
						draftScope={`new:${userKey}`}
						status="ready"
						model={model}
						recommendedModelIds={recommendedModelIds}
						onModelChange={handleModelChange}
						onSubmit={handleFirstSend}
						onStop={() => {}}
					/>
				</div>
			</div>
		</div>
	);
}

function ThreadAccessGate({
	clericalThreadId,
	focusMessageId,
}: {
	clericalThreadId: string;
	focusMessageId?: string;
}) {
	const navigate = useNavigate();
	const access = useQuery(api.agent.queries.getThreadAccessForChatPage, {
		clericalThreadId,
	});
	const startImpersonation = useMutation(
		api.staffImpersonation.startImpersonation,
	);
	const [switching, setSwitching] = useState(false);
	const [switchError, setSwitchError] = useState<string>();

	if (access === undefined) return <BlankChatPane />;
	if (access.status === "allowed") {
		return (
			<ActiveChat
				clericalThreadId={clericalThreadId}
				focusMessageId={focusMessageId}
			/>
		);
	}
	if (access.status === "denied") return <BlankChatPane />;

	const switchUser = async () => {
		setSwitching(true);
		setSwitchError(undefined);
		try {
			await startImpersonation({
				targetUserId: access.ownerUserId,
				reason: `Viewing chat thread ${clericalThreadId}`,
			});
			navigate(0);
		} catch (error) {
			setSwitchError(
				error instanceof Error ? error.message : "Unknown impersonation error",
			);
			setSwitching(false);
		}
	};

	return (
		<ThreadImpersonationPrompt
			currentUserEmail={access.currentUserEmail}
			ownerEmail={access.ownerEmail}
			onSwitch={() => void switchUser()}
			switching={switching}
			error={switchError}
		/>
	);
}

export default function ChatPage() {
	const { threadId } = useParams<{ threadId?: string }>();
	const [focusMessageId] = useQueryState("focus");
	const session = authClient.useSession();
	const userKey = (session.data?.user?.id as string | undefined) ?? "anon";

	if (threadId) {
		return (
			<ThreadAccessGate
				key={threadId}
				clericalThreadId={threadId}
				focusMessageId={focusMessageId ?? undefined}
			/>
		);
	}
	return <EmptyState userKey={userKey} />;
}
